import React, { useEffect, useState } from "react";
import { Newspaper, ExternalLink, Loader2 } from "lucide-react";
import api from "../lib/api";

const sentimentColor = (label) => {
  if (!label) return "#6B7068";
  const l = label.toLowerCase();
  if (l.includes("bullish") || l.includes("positive")) return "#5C8D73";
  if (l.includes("bearish") || l.includes("negative")) return "#C66B59";
  return "#547A8F";
};

const timeAgo = (ts) => {
  if (!ts) return "";
  // Alpha Vantage format: 20250504T153000
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})/.exec(ts);
  if (!match) return ts;
  const [, y, mo, d, h, mi] = match;
  const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:00Z`);
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function NewsFeed({ symbol, asset_type }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (symbol) params.set("symbol", symbol);
    if (asset_type) params.set("asset_type", asset_type);
    params.set("limit", "10");
    api.get(`/news?${params.toString()}`)
      .then((res) => { if (!cancel) setArticles(res.data.articles || []); })
      .catch((e) => { if (!cancel) setError(e.response?.data?.detail || "News unavailable"); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [symbol, asset_type]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-center gap-2 text-sm text-[#6B7068]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading news...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 text-sm text-[#6B7068]" data-testid="news-error">
        Couldn't load news right now. Alpha Vantage free tier allows ~25 calls/day.
      </div>
    );
  }

  if (!articles.length) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 text-sm text-[#6B7068]" data-testid="news-empty">
        No recent news for {symbol}.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="news-feed">
      {articles.map((a, i) => {
        const sentLabel = a.symbol_sentiment?.label || a.overall_sentiment_label;
        const color = sentimentColor(sentLabel);
        return (
          <a
            key={`${a.url}-${i}`}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`news-article-${i}`}
            className="block rounded-2xl bg-white border border-[#EAE6E1] p-5 transition-soft hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(42,47,43,0.05)] hover:border-[#547A8F]/40 group"
          >
            <div className="flex items-start gap-4">
              {a.banner_image && (
                <img
                  src={a.banner_image}
                  alt=""
                  className="w-24 h-24 rounded-xl object-cover flex-shrink-0 hidden sm:block"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B7068]">
                    {a.source || "News"}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-[#6B7068]" />
                  <span className="text-[10px] font-mono-ticker text-[#6B7068]">
                    {timeAgo(a.time_published)}
                  </span>
                  {sentLabel && (
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                      style={{ backgroundColor: `${color}15`, color }}
                    >
                      {sentLabel}
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-lg leading-snug text-[#2A2F2B] group-hover:text-[#547A8F] transition-soft">
                  {a.title}
                </h3>
                {a.summary && (
                  <p className="text-sm text-[#6B7068] mt-2 line-clamp-2 leading-relaxed">
                    {a.summary}
                  </p>
                )}
              </div>
              <ExternalLink className="w-4 h-4 text-[#6B7068] group-hover:text-[#547A8F] flex-shrink-0 mt-1" strokeWidth={1.5} />
            </div>
          </a>
        );
      })}
    </div>
  );
}
